import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * The creator app's single source of truth for "what does WTSN need from me?".
 *
 * The Dashboard's Needs Your Attention panel, the Operations room and the Rooms
 * grid all ask that question, and an alert that shows up twice with two
 * different counts is worse than no alert at all. So they all read this one
 * provider, which makes exactly one request (`GET /api/dashboard/creator`) for
 * the creator's profile, their outstanding actions and their content count.
 *
 * Mounted app-wide but inert for every other role: staff never fetch it.
 */

const CreatorHomeContext = createContext(null);

const EMPTY = {
  creator: null,
  attention: [],
  contentCount: null,
  openTaskCount: 0,
  loading: false,
  loaded: false,
  error: '',
  refresh: () => {},
};

export function CreatorHomeProvider({ children }) {
  const { token, user } = useAuth();
  const isCreator = user?.role === 'creator';

  const [state, setState] = useState({
    creator: null, attention: [], contentCount: null, openTaskCount: 0,
    loading: false, loaded: false, error: '',
  });

  const refresh = useCallback(async () => {
    if (!token || !isCreator) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await fetch('/api/dashboard/creator', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Could not load your dashboard');
      const data = await res.json();
      setState({
        creator: data.creator || null,
        attention: data.attention || [],
        contentCount: data.contentCount ?? null,
        openTaskCount: data.openTaskCount || 0,
        loading: false,
        loaded: true,
        error: '',
      });
    } catch (err) {
      // `loaded` still flips: a page waiting on this must be able to stop
      // showing skeletons and say something, rather than spin forever.
      setState((s) => ({ ...s, loading: false, loaded: true, error: err.message }));
    }
  }, [token, isCreator]);

  useEffect(() => { refresh(); }, [refresh]);

  // Coming back to the tab after acting on something elsewhere (finishing a
  // task on the Operations page, uploading against a request) should not leave
  // a stale count on the dashboard. Cheap: one request, only when the app is
  // actually brought back to the front.
  useEffect(() => {
    if (!isCreator) return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isCreator, refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return <CreatorHomeContext.Provider value={value}>{children}</CreatorHomeContext.Provider>;
}

export function useCreatorHome() {
  // A no-op shape rather than a throw, so a creator page can be rendered in a
  // test without standing the whole provider tree up.
  return useContext(CreatorHomeContext) || EMPTY;
}

/** Outstanding items only — the positive "all good" line is not a to-do. */
export function outstandingItems(attention) {
  return (attention || []).filter((i) => i.severity !== 'ok');
}
